import React from "react";
import {
  Card,
  Tag,
  Table,
  Space,
  Button,
  Tooltip,
  Alert,
  message,
} from "antd";
import {
  ShopOutlined,
  CopyOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ExclamationCircleOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";

const parseFailureReason = (reason) => {
  if (!reason) return null;
  if (typeof reason !== "string") return JSON.stringify(reason);
  try {
    // If it's a stringified python dict like "{'errorCode': '...', 'errorMsg': '...'}"
    const cleaned = reason
      .replace(/'/g, '"')
      .replace(/True/g, "true")
      .replace(/False/g, "false");
    const parsed = JSON.parse(cleaned);
    return parsed.errorMsg || parsed.message || parsed.errorCode || reason;
  } catch (e) {
    // Extract errorMsg if regex matches
    const match = reason.match(/'errorMsg':\s*'([^']+)'/);
    if (match) return match[1];
    return reason;
  }
};

const getStatusTag = (status, t) => {
  const s = String(status || "").toLowerCase();
  if (s === "success" || s === "completed") {
    return <Tag color="green" icon={<CheckCircleOutlined />}>{t ? t("orderDetails.statusSuccess", "Success") : "Success"}</Tag>;
  }
  if (s === "failed" || s === "fail") {
    return <Tag color="error" icon={<CloseCircleOutlined />}>{t ? t("orderDetails.statusFailed", "Failed") : "Failed"}</Tag>;
  }
  if (s === "canceled" || s === "cancelled") {
    return <Tag color="default" icon={<ExclamationCircleOutlined />}>{t ? t("orderDetails.statusCanceled", "Canceled") : "Canceled"}</Tag>;
  }
  return <Tag color="processing">{status ? (t ? t(`orderDetails.status${status.charAt(0).toUpperCase() + status.slice(1).toLowerCase()}`, status) : status) : (t ? t("orderDetails.statusPending", "Pending") : "Pending")}</Tag>;
};

const FulfillmentBySeller = ({ orders = [], isDarkMode = false }) => {
  const { t } = useTranslation();
  if (!orders || orders.length === 0) {
    return (
      <div
        style={{
          padding: "40px",
          textAlign: "center",
          color: "#8c8c8c",
          backgroundColor: isDarkMode ? "#1f1f1f" : "#fafafa",
          borderRadius: 12,
          border: "1px dashed #d9d9d9",
        }}
      >
        <ShopOutlined style={{ fontSize: 32, marginBottom: 8, color: "#bfbfbf" }} />
        <div>{t("orderDetails.noFulfillmentOrders", "No 1688 seller fulfillment orders found for this order.")}</div>
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {orders.map((seller, idx) => {
        const failureText = parseFailureReason(seller.failure_reason);
        const orderAmount = Number(seller.order_amount || 0).toFixed(2);
        const postFee = Number(seller.post_fee || 0).toFixed(2);

        const itemColumns = [
          {
            title: t("orderDetails.productItem", "Product Item"),
            key: "product",
            render: (_, item) => {
              const itemImg = item.image
                ? item.image.startsWith("http")
                  ? item.image
                  : `${process.env.REACT_APP_PUBLIC_MEDIA_URL || ""}${item.image}`
                : null;
              return (
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  {itemImg ? (
                    <img
                      src={itemImg}
                      alt={item.wege_sku || "Item"}
                      style={{
                        width: 44,
                        height: 44,
                        objectFit: "cover",
                        borderRadius: 6,
                        border: "1px solid #f0f0f0",
                      }}
                      onError={(e) => {
                        e.target.style.display = "none";
                      }}
                    />
                  ) : null}
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 13 }}>
                      {item.wege_sku || `Item #${item.id}`}
                    </div>
                    <div style={{ fontSize: 11, color: "#8c8c8c" }}>
                      {t("orderDetails.spec", "Spec:")} {item.spec_id ? `${item.spec_id.slice(0, 10)}...` : "-"}
                    </div>
                  </div>
                </div>
              );
            },
          },
          {
            title: t("orderDetails.colorSize", "Color / Size"),
            key: "variation",
            render: (_, item) => (
              <Space size={4} wrap>
                {item.color?.name && (
                  <Tag style={{ margin: 0, fontSize: 11 }}>{item.color.name}</Tag>
                )}
                {item.size?.name && (
                  <Tag color="blue" style={{ margin: 0, fontSize: 11 }}>
                    {item.size.name}
                  </Tag>
                )}
              </Space>
            ),
          },
          {
            title: t("orderDetails.qty", "Qty"),
            dataIndex: "quantity",
            key: "quantity",
            align: "center",
            render: (qty) => <strong style={{ color: "#1677ff" }}>×{qty}</strong>,
          },
          {
            title: t("orderDetails.price1688", "1688 Price"),
            dataIndex: "price",
            key: "price",
            render: (price) => `${Number(price || 0).toFixed(2)} ¥`,
          },
          {
            title: t("orderDetails.status", "Status"),
            dataIndex: "status",
            key: "status",
            render: (status, record) => {
              const itemFailure = parseFailureReason(record.failure_reason);
              return (
                <div>
                  {getStatusTag(status, t)}
                  {itemFailure && (
                    <Tooltip title={itemFailure}>
                      <div
                        style={{
                          fontSize: 11,
                          color: "#ff4d4f",
                          marginTop: 2,
                          maxWidth: 160,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {itemFailure}
                      </div>
                    </Tooltip>
                  )}
                </div>
              );
            },
          },
        ];

        return (
          <Card
            key={seller.id || idx}
            style={{
              borderRadius: 12,
              boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
              border: "1px solid #e8e8e8",
              background: isDarkMode ? "#1f1f1f" : "#fff",
            }}
            bodyStyle={{ padding: "16px 20px" }}
          >
            {/* Header info */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 10,
                borderBottom: "1px solid #f0f0f0",
                paddingBottom: 12,
                marginBottom: 12,
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                <span style={{ fontWeight: 700, fontSize: 15 }}>
                  <ShopOutlined style={{ marginRight: 6, color: "#1677ff" }} />
                  {t("orderDetails.seller", { id: idx + 1, defaultValue: `Seller #${idx + 1}` })}
                </span>

                {seller.store && (
                  <Tag color="cyan">{t("orderDetails.store", "Store")}: {seller.store}</Tag>
                )}

                {getStatusTag(seller.status, t)}

                {seller.order_id && (
                  <div style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                    <span style={{ fontSize: 12, color: "#8c8c8c" }}>{t("orderDetails.order1688", "1688 Order:")}</span>
                    <span
                      style={{
                        fontFamily: "monospace",
                        fontSize: 12,
                        fontWeight: 600,
                        color: "#1677ff",
                      }}
                    >
                      {seller.order_id}
                    </span>
                    <Tooltip title={t("orderDetails.copy1688OrderId", "Copy 1688 Order ID")}>
                      <Button
                        type="text"
                        size="small"
                        icon={<CopyOutlined style={{ color: "#1677ff" }} />}
                        onClick={() => {
                          navigator.clipboard.writeText(seller.order_id);
                          message.success(t("orderDetails.order1688IdCopied", "1688 Order ID copied!"));
                        }}
                      />
                    </Tooltip>
                  </div>
                )}
              </div>

              {/* Price / Post fee */}
              <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
                <div>
                  <span style={{ fontSize: 12, color: "#8c8c8c" }}>{t("orderDetails.amount", "Amount:")} </span>
                  <span style={{ fontWeight: 700, fontSize: 14 }}>
                    {orderAmount} ¥
                  </span>
                </div>
                <div>
                  <span style={{ fontSize: 12, color: "#8c8c8c" }}>{t("orderDetails.postFee", "Post Fee:")} </span>
                  <span style={{ fontWeight: 500, fontSize: 13 }}>
                    {postFee} ¥
                  </span>
                </div>
              </div>
            </div>

            {/* Logistics Bill No */}
            {seller.logistics_bill_no && (
              <div
                style={{
                  background: isDarkMode ? "#141414" : "#f9f9f9",
                  padding: "6px 12px",
                  borderRadius: 6,
                  border: "1px dashed #d9d9d9",
                  marginBottom: 12,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ fontSize: 12, color: "#8c8c8c" }}>
                    Logistics Waybill:
                  </span>
                  <span
                    style={{
                      fontFamily: "monospace",
                      fontWeight: 600,
                      color: "#52c41a",
                    }}
                  >
                    {seller.logistics_bill_no}
                  </span>
                </div>
                <Tooltip title="Copy Waybill">
                  <Button
                    type="text"
                    size="small"
                    icon={<CopyOutlined style={{ color: "#52c41a" }} />}
                    onClick={() => {
                      navigator.clipboard.writeText(seller.logistics_bill_no);
                      message.success("Logistics bill copied!");
                    }}
                  />
                </Tooltip>
              </div>
            )}

            {/* Failure reason alert */}
            {failureText && (
              <Alert
                type="error"
                showIcon
                message="1688 Placement Error"
                description={failureText}
                style={{ marginBottom: 12, borderRadius: 8 }}
              />
            )}

            {/* Items Table */}
            {seller.items && seller.items.length > 0 && (
              <Table
                dataSource={seller.items}
                columns={itemColumns}
                rowKey="id"
                pagination={false}
                size="small"
              />
            )}
          </Card>
        );
      })}
    </div>
  );
};

export default FulfillmentBySeller;
